import {
  Form,
  NumericFormatField,
  SubmitButton,
  TextAreaInputField,
} from '@/components';
import { useProject } from '@/features/project/_hooks/project';
import { ProjectOtherNotesFormSchema } from '@/features/project/projects/_models/project';
import { useForm, useId } from '@/hooks';
import type { sheetTypeEnum } from '@/utils/enum';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type FormValues = z.infer<typeof ProjectOtherNotesFormSchema>;

type Props = {
  initialValues?: Partial<FormValues>;
  onSubmit: (values: FormValues) => void;
  type?: 'basic' | 'skipped-review';
  sheetType: z.infer<typeof sheetTypeEnum>;
};

export const ProjectOtherNotesForm: FC<Props> = ({
  initialValues,
  onSubmit,
}) => {
  const { id } = useId();
  const { data: project } = useProject(id);
  const fiscalYear = project?.fiscalYear ?? 2025;
  const { t } = useTranslation();
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isSubmitSuccessful },
  } = useForm<FormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(ProjectOtherNotesFormSchema),
  });

  return (
    <Form
      leavePageAlertCondition={{
        isDirty,
        isSubmitting,
        isSubmitSuccessful,
      }}
      layout="vertical"
      onFinish={handleSubmit(onSubmit)}
    >
      <TextAreaInputField
        fallbackValue={null}
        name="increaseDecreaseMainReason"
        label={t('increase-decrease-main-reason')}
        control={control}
        errors={errors}
        rows={5}
      />

      <NumericFormatField
        name="totalCurrentYearBiayaTenaga"
        label={fiscalYear + t('total-biaya-program')}
        control={control}
        decimalScale={10}
        errors={errors}
      />
      <NumericFormatField
        name="totalPreviousYearBiayaTenaga"
        label={fiscalYear - 1 + t('total-biaya-program')}
        decimalScale={10}
        control={control}
        errors={errors}
      />

      <TextAreaInputField
        fallbackValue={null}
        name="privateReason"
        label={t('revenue-source')}
        control={control}
        errors={errors}
        rows={5}
      />

      <TextAreaInputField
        fallbackValue={null}
        name="otherSpecialNotes"
        label={t('other-special-notes')}
        control={control}
        errors={errors}
        rows={5}
      />

      <SubmitButton loading={isSubmitting} disabled={!isDirty} />
    </Form>
  );
};
